import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Clock, MapPin, Phone, Mail, Globe, Users, ExternalLink, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const AdminBeeClubs = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/bee-clubs`, { headers });
      if (res.ok) {
        const data = await res.json();
        setClubs(data.clubs || []);
      }
    } catch { toast.error('Failed to load clubs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClubs(); }, []);

  const approveClub = async (clubId) => {
    try {
      const res = await fetch(`${API}/api/admin/bee-clubs/${clubId}/approve`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }
      });
      if (res.ok) { toast.success('Club approved and now public'); fetchClubs(); }
    } catch { toast.error('Failed to approve'); }
  };

  const rejectClub = async (clubId) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return;
    try {
      const res = await fetch(`${API}/api/admin/bee-clubs/${clubId}/reject`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (res.ok) { toast.success('Club rejected'); fetchClubs(); }
    } catch { toast.error('Failed to reject'); }
  };

  const filtered = filter === 'all' ? clubs : clubs.filter(c => c.status === filter);
  const pendingCount = clubs.filter(c => c.status === 'pending').length;

  return (
    <div className="max-w-7xl" data-testid="admin-bee-clubs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Users className="h-6 w-6 text-amber-600" />
            Bee Club Approvals
          </h1>
          <p className="text-sm text-stone-500 mt-1">Review and approve bee club listings before they go public.</p>
        </div>
        {pendingCount > 0 && (
          <span className="bg-amber-100 text-amber-700 text-sm font-bold px-3 py-1.5 rounded-full">
            {pendingCount} pending
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'all', label: `All (${clubs.length})` },
          { key: 'pending', label: `Pending (${clubs.filter(c => c.status === 'pending').length})` },
          { key: 'approved', label: `Approved (${clubs.filter(c => c.status === 'approved').length})` },
          { key: 'rejected', label: `Rejected (${clubs.filter(c => c.status === 'rejected').length})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f.key ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Club List */}
      {loading ? (
        <div className="text-center py-12 text-stone-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <Users className="h-12 w-12 text-stone-200 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700">No Clubs</h3>
          <p className="text-sm text-stone-500 mt-1">{filter === 'pending' ? 'No pending submissions.' : 'No clubs found.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(club => (
            <div key={club.id} className="bg-white rounded-xl border border-stone-200 p-5" data-testid={`club-${club.id}`}>
              <div className="flex items-start gap-4">
                {/* Logo */}
                {club.logo_url ? (
                  <img src={club.logo_url.startsWith('http') ? club.logo_url : `${API}${club.logo_url}`} alt="" className="w-14 h-14 rounded-xl object-cover border flex-shrink-0" onError={e => e.target.style.display='none'} />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0 text-purple-700 font-bold text-lg">
                    {club.club_name?.charAt(0)?.toUpperCase() || 'B'}
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-stone-900">{club.club_name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[club.status] || 'bg-stone-100 text-stone-500'}`}>
                      {club.status?.toUpperCase()}
                    </span>
                  </div>

                  <div className="text-sm text-stone-500 flex flex-wrap gap-x-4 gap-y-1">
                    {club.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {club.city}, {club.state}</span>}
                    {club.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {club.email}</span>}
                    {club.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {club.phone}</span>}
                    {club.website && <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> <a href={club.website} target="_blank" rel="noopener" className="underline">{club.website.replace(/^https?:\/\//, '').slice(0, 30)}</a></span>}
                    {club.member_count && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {club.member_count} members</span>}
                  </div>

                  {club.description && <p className="text-xs text-stone-400 mt-1 line-clamp-2">{club.description}</p>}

                  {/* Submitter info */}
                  <div className="mt-2 text-xs text-stone-400 bg-stone-50 rounded-lg p-2 space-y-0.5">
                    <p><strong>Submitted by:</strong> {club.user_name || 'Unknown'} ({club.user_email || 'No email'})</p>
                    {club.verification_email && <p><strong>Verification email:</strong> {club.verification_email}</p>}
                    {club.verification_phone && <p><strong>Verification phone:</strong> {club.verification_phone}</p>}
                    {club.updated_at && <p><strong>Last updated:</strong> {new Date(club.updated_at).toLocaleString()}</p>}
                    {club.rejection_reason && <p className="text-red-600"><strong>Rejection reason:</strong> {club.rejection_reason}</p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {club.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => approveClub(club.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3" data-testid={`approve-${club.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectClub(club.id)} className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-8 px-3" data-testid={`reject-${club.id}`}>
                        <X className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {club.status === 'rejected' && (
                    <Button size="sm" onClick={() => approveClub(club.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3">
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {club.status === 'approved' && (
                    <Button size="sm" variant="outline" onClick={() => rejectClub(club.id)} className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-8 px-3">
                      <X className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBeeClubs;
